import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router';
import {
    Grid,
    Box,
    Button,
    Paper,
    Typography,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Chip,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    Switch,
    FormControlLabel,
} from '@mui/material';
import {
    Add as AddIcon,
    Topic as TopicIcon,
    MoreVert as MoreIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Quiz as QuizIcon,
} from '@mui/icons-material';
import UpdatePopup from '../../components/UpdatePopup.jsx';

const TopicCard = ({topic, courseId, onTopicUpdated, onTopicDeleted, onEditTopic, apiClient}) => {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [confirmationText, setConfirmationText] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [publishing, setPublishing] = useState(false);

    const menuOpen = Boolean(anchorEl);

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDeleteClick = () => {
        setDeleteDialogOpen(true);
        handleMenuClose();
    };

    const handleDeleteDialogClose = () => {
        if (!deleting) {
            setDeleteDialogOpen(false);
            setConfirmationText('');
            setDeleteError(null);
        }
    };

    const handleDeleteTopic = async () => {
        if (confirmationText !== topic.name) {
            setDeleteError('Topic title does not match. Please type the exact topic title.');
            return;
        }

        setDeleting(true);
        setDeleteError(null);

        try {
            await apiClient.delete(`/api/courses/${courseId}/topics/${topic.id}`);
            onTopicDeleted(topic.id);
            setDeleteDialogOpen(false);
            setConfirmationText('');
        } catch (error) {
            console.error('Error deleting topic:', error);
            setDeleteError(error.message || 'Failed to delete topic');
        } finally {
            setDeleting(false);
        }
    };

    const handleTogglePublished = async () => {
        setPublishing(true);
        try {
            const updatedTopic = await apiClient.patch(`/api/courses/${courseId}/topics/${topic.id}`, {
                is_published: !topic.is_published
            });
            onTopicUpdated(updatedTopic);
        } catch (error) {
            console.error('Error updating topic:', error);
        } finally {
            setPublishing(false);
        }
    };

    const handleViewTopic = () => {
        navigate(`/teacher/course/${courseId}/topic/${topic.id}`);
    };

    const handleEditTopic = () => {
        onEditTopic(topic);
        handleMenuClose();
    };

    return (
        <>
            <Card sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                <CardContent sx={{flexGrow: 1}}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2}}>
                        <Typography variant="h6" component="h3" gutterBottom>
                            {topic.name}
                        </Typography>
                        <IconButton size="small" onClick={handleMenuClick}>
                            <MoreIcon/>
                        </IconButton>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{mb: 2, minHeight: '40px'}}>
                        {topic.description || 'No description available'}
                    </Typography>

                    <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2}}>
                        <Chip
                            icon={<QuizIcon/>}
                            label={topic.question_count === 0 ? 'No Questions' : `${topic.question_count} Question${topic.question_count === 1 ? '' : 's'}`}
                            size="small"
                            color={topic.question_count === 0 ? 'warning' : 'success'}
                            variant="outlined"
                        />
                        <Chip
                            label={topic.is_published ? 'Published' : 'Draft'}
                            size="small"
                            color={topic.is_published ? 'success' : 'default'}
                            variant="outlined"
                        />
                    </Box>
                </CardContent>

                <CardActions sx={{justifyContent: 'space-between', px: 2, pb: 2}}>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={handleViewTopic}
                        fullWidth
                        sx={{mr: 1}}
                    >
                        View Topic
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleEditTopic}
                        fullWidth
                    >
                        Edit
                    </Button>
                </CardActions>
            </Card>

            {/* Menu */}
            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                transformOrigin={{horizontal: 'right', vertical: 'top'}}
                anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
            >
                <MenuItem onClick={handleEditTopic}>
                    <ListItemIcon>
                        <EditIcon fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>Edit Topic</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={handleDeleteClick}
                    sx={{color: 'error.main'}}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" sx={{color: 'error.main'}}/>
                    </ListItemIcon>
                    <ListItemText>Delete Topic</ListItemText>
                </MenuItem>
            </Menu>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{color: 'error.main'}}>
                    Delete Topic: {topic.name}
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{mb: 3}}>
                        <Typography variant="body2" sx={{fontWeight: 'bold', mb: 1}}>
                            This action cannot be undone!
                        </Typography>
                        <Typography variant="body2">
                            Deleting this topic will:
                        </Typography>
                        <Box component="ul" sx={{mt: 1, mb: 0, pl: 2}}>
                            <li>Remove all questions and content</li>
                            <li>Delete all student progress on this topic</li>
                            <li>Remove all analytics data</li>
                        </Box>
                    </Alert>

                    {deleteError && (
                        <Alert severity="error" sx={{mb: 2}}>
                            {deleteError}
                        </Alert>
                    )}

                    <Typography variant="body1" sx={{mb: 2}}>
                        To confirm deletion, please type the topic title exactly:
                    </Typography>

                    <Typography variant="body2" sx={{
                        mb: 2,
                        fontWeight: 'bold',
                        fontFamily: 'monospace',
                        backgroundColor: 'grey.100',
                        p: 1,
                        borderRadius: 1
                    }}>
                        {topic.name}
                    </Typography>

                    <TextField
                        fullWidth
                        label="Type topic title to confirm"
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                        disabled={deleting}
                        variant="outlined"
                        error={deleteError && confirmationText !== topic.name}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteDialogClose} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteTopic}
                        color="error"
                        variant="contained"
                        disabled={deleting || confirmationText !== topic.name}
                        startIcon={deleting ? <CircularProgress size={20}/> : <DeleteIcon/>}
                    >
                        {deleting ? 'Deleting...' : 'Delete Topic'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const TopicsTab = ({courseId, apiClient, onUpdate}) => {
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState(null);
    const [topicData, setTopicData] = useState({
        name: '',
        description: '',
    });

    // Edit popup state
    const [showEditTopic, setShowEditTopic] = useState(false);
    const [selectedTopic, setSelectedTopic] = useState(null);

    const fetchTopics = async () => {
        try {
            const topicsResponse = await apiClient.get(`/api/courses/${courseId}/topics`);
            const topicsList = topicsResponse.topics || topicsResponse || [];
            // Sort topics alphabetically by name
            const sortedTopics = topicsList.sort((a, b) =>
                a.name.localeCompare(b.name, 'de', { sensitivity: 'base' })
            );
            setTopics(sortedTopics);
        } catch (error) {
            console.error('Error fetching topics:', error);
            setTopics([]);
        }
    };

    useEffect(() => {
        const loadTopics = async () => {
            setLoading(true);
            await fetchTopics();
            setLoading(false);
        };

        if (courseId && apiClient) {
            loadTopics();
        }
    }, [courseId, apiClient]);

    const handleCreateTopic = async () => {
        if (!topicData.name.trim() || !topicData.description.trim()) {
            setCreateError('Both title and description are required');
            return;
        }

        setCreating(true);
        setCreateError(null);

        try {
            await apiClient.post(`/api/courses/${courseId}/topics`, {
                name: topicData.name.trim(),
                description: topicData.description.trim(),
                course_id: courseId,
            });

            await fetchTopics();
            onUpdate();

            setTopicData({name: '', description: ''});
            setCreateDialogOpen(false);
        } catch (error) {
            console.error('Error creating topic:', error);
            setCreateError(error.message || 'Failed to create topic');
        } finally {
            setCreating(false);
        }
    };

    const handleTopicUpdated = async (updatedTopic) => {
        await fetchTopics();
        onUpdate();
    };

    const handleTopicDeleted = async (topicId) => {
        await fetchTopics();
        onUpdate();
    };

    const handleEditTopic = (topic) => {
        setSelectedTopic(topic);
        setShowEditTopic(true);
    };

    const handleCloseEditTopic = () => {
        setShowEditTopic(false);
        setSelectedTopic(null);
    };

    const handleDialogClose = () => {
        if (!creating) {
            setCreateDialogOpen(false);
            setTopicData({name: '', description: ''});
            setCreateError(null);
        }
    };

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', my: 4}}>
                <CircularProgress/>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Typography variant="h6" sx={{display: 'flex', alignItems: 'center'}}>
                    <TopicIcon sx={{mr: 1}}/>
                    Course Topics
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon/>}
                    onClick={() => setCreateDialogOpen(true)}
                >
                    Create Topic
                </Button>
            </Box>

            {topics.length === 0 ? (
                <Paper sx={{p: 3, textAlign: 'center'}}>
                    <Typography variant="body1" gutterBottom>
                        No topics created yet.
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon/>}
                        onClick={() => setCreateDialogOpen(true)}
                        sx={{mt: 2}}
                    >
                        Create Your First Topic
                    </Button>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {topics.map((topic) => (
                        <Grid size={{xs: 12, sm: 6, md: 4}} key={topic.id}>
                            <TopicCard
                                topic={topic}
                                courseId={courseId}
                                onTopicUpdated={handleTopicUpdated}
                                onTopicDeleted={handleTopicDeleted}
                                onEditTopic={handleEditTopic}
                                apiClient={apiClient}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Create Topic Dialog */}
            <Dialog
                open={createDialogOpen}
                onClose={handleDialogClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    component: 'form',
                    onSubmit: (e) => {
                        e.preventDefault();
                        handleCreateTopic();
                    },
                }}
            >
                <DialogTitle>Create New Topic</DialogTitle>
                <DialogContent>
                    {createError && (
                        <Alert severity="error" sx={{mb: 2}}>
                            {createError}
                        </Alert>
                    )}

                    <TextField
                        autoFocus
                        margin="dense"
                        label="Topic Title"
                        fullWidth
                        variant="outlined"
                        value={topicData.name}
                        onChange={(e) => setTopicData({...topicData, name: e.target.value})}
                        disabled={creating}
                        required
                        sx={{mb: 2}}
                    />

                    <TextField
                        margin="dense"
                        label="Topic Description"
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={3}
                        value={topicData.description}
                        onChange={(e) => setTopicData({...topicData, description: e.target.value})}
                        disabled={creating}
                        required
                        placeholder="Describe what this topic covers"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} disabled={creating}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={creating || !topicData.name.trim() || !topicData.description.trim()}
                        startIcon={creating ? <CircularProgress size={20}/> : <AddIcon/>}
                    >
                        {creating ? 'Creating...' : 'Create Topic'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Topic Popup */}
            {selectedTopic && (
                <UpdatePopup
                    open={showEditTopic}
                    onClose={handleCloseEditTopic}
                    onUpdate={handleTopicUpdated}
                    entityType="topic"
                    entityData={selectedTopic}
                    apiClient={apiClient}
                    courseId={courseId}
                    topicId={selectedTopic.id}
                />
            )}
        </Box>
    );
};

export default TopicsTab;